import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic,
  Square,
  Pause,
  Play,
  Check,
  AlertTriangle,
  Sparkles,
  RotateCcw,
  ChevronDown,
  Wrench,
  Loader2,
  FileAudio,
  ArrowRightLeft,
  X,
} from "lucide-react";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAudioRecorder, formatClock } from "@/hooks/useAudioRecorder";
import { uploadAudio, analyseVoiceReport } from "@/lib/voice";
import { fileUrl } from "@/lib/files";
import { formatDate, num, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";

const HANDOFF_KEY = "vms_voice_handoff";

/** Hand a finished analysis over to the other report form (service <-> jobb) without re-recording. */
export const setVoiceHandoff = (data) => {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(data));
  } catch (e) {
    /* ignore */
  }
};
export const takeVoiceHandoff = () => {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY);
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const TIPS = {
  service: ["Maskin og kunde", "Timetal", "Kva du gjorde", "Avvik du fann", "Deler og oljer brukt", "Neste service"],
  jobb: ["Maskin eller kunde", "Kva jobben gjaldt", "Kva du gjorde", "Timar brukt", "Stad", "Oppfølging"],
};

const ERR_TEXT = {
  "not-allowed": "Mikrofon-tilgang vart avvist. Gi appen tilgang til mikrofonen i nettlesaren og prøv igjen.",
  "no-mic": "Fann ingen mikrofon på denne eininga.",
  unsupported: "Nettlesaren støttar ikkje lydopptak. Bruk Safari (iPhone) eller Chrome (Android).",
  recorder: "Opptaket feila. Prøv igjen.",
  failed: "Kunne ikkje starte opptak. Prøv igjen.",
};

/* ------------------------------------------------------------------ */
/*  Waveform (rolling level history)                                   */
/* ------------------------------------------------------------------ */
const Waveform = ({ levels, active }) => (
  <div className="flex h-14 items-center justify-center gap-[3px]" aria-hidden="true" data-testid="voice-waveform">
    {levels.map((v, i) => (
      <span
        key={i}
        className={cn("w-[3px] rounded-full transition-[height,opacity] duration-100", active ? "bg-primary" : "bg-muted-foreground/30")}
        style={{ height: `${Math.max(4, Math.round(v * 56))}px`, opacity: active ? 0.45 + v * 0.55 : 0.5 }}
      />
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Processing steps                                                   */
/* ------------------------------------------------------------------ */
const StepRow = ({ label, sub, status }) => (
  <div className="flex items-center gap-3 py-2" data-testid={`voice-step-${status}`}>
    <span
      className={cn(
        "grid h-8 w-8 shrink-0 place-items-center rounded-full border",
        status === "done" && "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]",
        status === "active" && "border-primary text-primary",
        status === "todo" && "text-muted-foreground/60"
      )}
    >
      {status === "done" ? <Check className="h-4 w-4" /> : status === "active" ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="h-2 w-2 rounded-full bg-current" />}
    </span>
    <span className="min-w-0">
      <span className={cn("block text-sm font-semibold", status === "todo" && "text-muted-foreground")}>{label}</span>
      {sub && <span className="block text-xs text-muted-foreground">{sub}</span>}
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Review helpers                                                     */
/* ------------------------------------------------------------------ */
const Section = ({ id, title, children, include, onToggle, disabled }) => (
  <div className={cn("min-w-0 rounded-xl border p-3 transition-opacity", include === false && "opacity-55")} data-testid={`voice-section-${id}`}>
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
      {onToggle && (
        <Switch checked={include !== false} onCheckedChange={onToggle} disabled={disabled} aria-label={`Bruk ${title}`} data-testid={`voice-include-${id}`} />
      )}
    </div>
    <div className="mt-1.5 text-sm">{children}</div>
  </div>
);

const Confidence = ({ level }) => {
  const map = { høg: "status-ok", middels: "status-upcoming", låg: "status-overdue" };
  if (!level || !map[level]) return null;
  return <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-semibold", map[level])}>{level === "høg" ? "Sikker" : level === "middels" ? "Truleg" : "Usikker"}</span>;
};

/* ------------------------------------------------------------------ */
/*  Main dialog                                                        */
/* ------------------------------------------------------------------ */
/**
 * VoiceReportDialog – record → upload → whisper-1 → AI structures the report → user reviews → apply.
 *
 * props:
 *  reportType  "service" | "jobb"
 *  machines    list from /machines (to show a name for the matched machine)
 *  machineId   machine already chosen in the form (passed to the AI as context)
 *  checklist   service checklist labels (for matching)
 *  onApply(result, include)  called with the analysis + which sections to use; returns nothing
 *  handoff     optional {file_id, transcript, duration_s} to start directly in analysis (from the other form)
 */
export const VoiceReportDialog = ({ open, onOpenChange, reportType = "service", machines = [], machineId, checklist = [], onApply, handoff }) => {
  const navigate = useNavigate();
  const rec = useAudioRecorder();
  const [phase, setPhase] = useState("record"); // record | processing | review | error
  const [uploadPct, setUploadPct] = useState(0);
  const [aiStep, setAiStep] = useState(1); // 1 upload · 2 whisper · 3 structure
  const [fileRec, setFileRec] = useState(null); // uploaded file record
  const [result, setResult] = useState(null);
  const [include, setInclude] = useState({});
  const [useMachine, setUseMachine] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const handoffRef = useRef(null);
  const aiTimerRef = useRef(0);

  const analyse = useCallback(
    async ({ fileId, transcript, durationS }) => {
      setPhase("processing");
      setAiStep(2);
      clearTimeout(aiTimerRef.current);
      aiTimerRef.current = setTimeout(() => setAiStep(3), transcript ? 400 : 6000);
      try {
        const res = await analyseVoiceReport({ fileId, transcript, reportType, machineId, checklist, today: todayISO() });
        res.duration_s = res.duration_s || durationS || null;
        setResult(res);
        setInclude({
          machine: !!res.machine?.matched_machine_id,
          service_type: !!res.service_type,
          hours: res.machine_hours != null,
          checklist: (res.checklist_done || []).length > 0,
          work: (res.work_performed || []).length > 0,
          parts: (res.parts_used || []).length > 0,
          defects: !!res.defects,
          next: res.next_service_hours != null || !!res.next_service_date,
          control: !!res.control_done,
          title: !!res.title,
          description: !!res.description,
          hours_spent: res.hours_spent != null,
          location: !!res.location,
          follow_up: !!res.follow_up,
        });
        setUseMachine(!!res.machine?.matched_machine_id);
        setPhase("review");
      } catch (e) {
        setErrMsg(e?.response?.data?.detail || "AI-tenesta svarte ikkje. Opptaket er lagra – prøv igjen.");
        setPhase("error");
      } finally {
        clearTimeout(aiTimerRef.current);
      }
    },
    [reportType, machineId, checklist]
  );

  // Reset when opened; start straight into analysis when a handoff is provided
  useEffect(() => {
    if (!open) {
      rec.reset();
      clearTimeout(aiTimerRef.current);
      return;
    }
    setResult(null);
    setErrMsg("");
    setUploadPct(0);
    setShowTranscript(false);
    if (handoff && handoff !== handoffRef.current) {
      handoffRef.current = handoff;
      setFileRec({ id: handoff.file_id, duration_s: handoff.duration_s });
      analyse({ fileId: handoff.file_id, transcript: handoff.transcript, durationS: handoff.duration_s });
    } else {
      setFileRec(null);
      setPhase("record");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the recorder produces a blob -> upload -> analyse
  useEffect(() => {
    if (rec.state !== "stopped" || !rec.blob || phase !== "record") return;
    if (rec.seconds < 1.5) {
      toast.info("Opptaket vart for kort. Hald inne og snakk i minst eit par sekund.");
      rec.reset();
      return;
    }
    (async () => {
      setPhase("processing");
      setAiStep(1);
      setUploadPct(0);
      try {
        const f = await uploadAudio(rec.blob, { durationS: rec.seconds, onProgress: setUploadPct });
        setFileRec(f);
        await analyse({ fileId: f.id, durationS: rec.seconds });
      } catch (e) {
        setErrMsg(e?.response?.data?.detail || "Kunne ikkje laste opp opptaket. Sjekk nettet og prøv igjen.");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.state, rec.blob]);

  useEffect(() => {
    if (rec.state === "error" && rec.error) toast.error(ERR_TEXT[rec.error] || ERR_TEXT.failed);
  }, [rec.state, rec.error]);

  const matched = useMemo(() => {
    const id = result?.machine?.matched_machine_id;
    return id ? machines.find((m) => m.id === id) || { id, label: result.machine.matched_label, customer_name: result.machine.matched_customer_name } : null;
  }, [result, machines]);

  const otherType = reportType === "service" ? "jobb" : "service";
  const hasContent = result && (result.title || result.description || (result.work_performed || []).length > 0 || (result.checklist_done || []).length > 0 || result.defects);
  const suggestsOther = hasContent && result.report_type_guess && result.report_type_guess !== reportType;

  const apply = () => {
    if (!result) return;
    onApply?.(result, { ...include, machine: include.machine && useMachine }, {
      file_id: fileRec?.id || result.file_id || null,
      duration_s: result.duration_s || fileRec?.duration_s || null,
      transcript: result.transcript,
      summary: result.summary,
    });
    onOpenChange(false);
    toast.success("Rapporten er fylt ut frå opptaket – kontroller felta før du lagrar.");
  };

  const switchForm = () => {
    setVoiceHandoff({ file_id: fileRec?.id || result.file_id, transcript: result.transcript, duration_s: result.duration_s });
    onOpenChange(false);
    navigate(otherType === "jobb" ? "/rapportar/ny-jobb?voice=handoff" : "/rapportar/ny-service?voice=handoff");
  };

  const retryAnalysis = () => {
    if (fileRec?.id || result?.transcript) analyse({ fileId: fileRec?.id, transcript: result?.transcript, durationS: fileRec?.duration_s });
    else {
      rec.reset();
      setPhase("record");
    }
  };

  const recording = rec.state === "recording";
  const paused = rec.state === "paused";
  const requesting = rec.state === "requesting";
  const progressPct = Math.min(100, (rec.seconds / rec.maxSeconds) * 100);

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        className="flex max-h-[92vh] flex-col gap-0 p-0 sm:max-w-lg"
        drawerClassName="max-h-[94dvh]"
        data-testid="voice-report-dialog"
        onInteractOutside={(e) => (recording || paused || phase === "processing") && e.preventDefault()}
      >
        <ResponsiveDialogHeader className="md:border-b md:px-6 md:pb-4 md:pt-6">
          <ResponsiveDialogTitle className="flex items-center gap-2 font-heading">
            <Sparkles className="h-5 w-5 text-primary" />
            {phase === "review" ? "Kontroller forslaget" : phase === "processing" ? "Lagar rapport …" : "Snakk inn rapporten"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {phase === "record" && "Fortel fritt kva du har gjort. AI skriv ned og fyller ut felta – du kontrollerer alt før lagring."}
            {phase === "processing" && "Dette tek vanlegvis 10–20 sekund."}
            {phase === "review" && (result?.summary || "AI-forslag frå opptaket. Slå av det du ikkje vil bruke.")}
            {phase === "error" && "Noko gjekk gale."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* ---------------- RECORD ---------------- */}
        {phase === "record" && (
          <>
            <ResponsiveDialogBody className="md:px-6 md:py-5">
              <div className="flex flex-col items-center gap-4 pt-1">
                <div className="font-heading text-5xl font-extrabold tabular-nums tracking-tight" data-testid="voice-timer" aria-live="off">
                  {formatClock(rec.seconds)}
                </div>
                <Waveform levels={rec.levels} active={recording} />
                <div className="relative">
                  {recording && <span className="voice-ring absolute inset-0 rounded-full border-2 border-primary" aria-hidden="true" />}
                  {recording && <span className="voice-ring absolute inset-0 rounded-full border-2 border-primary [animation-delay:0.6s]" aria-hidden="true" />}
                  <button
                    type="button"
                    onClick={recording ? rec.pause : paused ? rec.resume : rec.start}
                    disabled={requesting}
                    data-testid={recording ? "voice-pause-button" : paused ? "voice-resume-button" : "voice-record-button"}
                    aria-label={recording ? "Pause opptak" : paused ? "Fortsett opptak" : "Start opptak"}
                    className={cn(
                      "pressable relative grid h-24 w-24 place-items-center rounded-full text-white shadow-[0_16px_40px_rgba(255,78,0,0.35)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40",
                      recording ? "bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]" : "bg-primary text-[#0B0D10]"
                    )}
                  >
                    {requesting ? <Loader2 className="h-9 w-9 animate-spin" /> : recording ? <Pause className="h-9 w-9" /> : paused ? <Play className="h-9 w-9 translate-x-0.5" /> : <Mic className="h-10 w-10" />}
                  </button>
                </div>
                <div className="text-sm font-medium" data-testid="voice-state-text">
                  {requesting && "Ber om mikrofon-tilgang …"}
                  {recording && <span className="flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-primary" /> Tek opp – snakk fritt</span>}
                  {paused && "Pausa – trykk for å fortsetje"}
                  {rec.state === "idle" && "Trykk for å starte opptaket"}
                  {rec.state === "error" && <span className="text-destructive">{ERR_TEXT[rec.error] || ERR_TEXT.failed}</span>}
                </div>
                {(recording || paused) && (
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${progressPct}%` }} />
                  </div>
                )}
              </div>

              <div className="rounded-xl border bg-muted/40 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Nemn gjerne</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {TIPS[reportType].map((t) => (
                    <span key={t} className="rounded-full border bg-card px-2.5 py-1 text-xs">{t}</span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  T.d. «500-timers service på Volvoen hos Åsane Graving, maskin 204, 2 350 timar. Bytta motorolje og filter, 25 liter 15W-40. Lekkasje ved bomsylinder – må følgjast opp. Neste service om 500 timar.»
                </p>
              </div>
            </ResponsiveDialogBody>
            <ResponsiveDialogFooter className="md:border-t md:px-6 md:py-3">
              {recording || paused ? (
                <>
                  <Button variant="outline" onClick={() => { rec.reset(); }} data-testid="voice-discard-button"><X /> Forkast</Button>
                  <Button onClick={rec.stop} data-testid="voice-stop-button"><Square className="fill-current" /> Stopp og lag rapport</Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="voice-cancel-button">Avbryt</Button>
              )}
            </ResponsiveDialogFooter>
          </>
        )}

        {/* ---------------- PROCESSING ---------------- */}
        {phase === "processing" && (
          <ResponsiveDialogBody className="pb-6 md:px-6 md:py-5">
            <div className="divide-y rounded-xl border px-3">
              <StepRow label="Lastar opp opptaket" sub={aiStep === 1 ? `${uploadPct} %` : `${formatClock(fileRec?.duration_s || rec.seconds)} lagra`} status={aiStep > 1 ? "done" : "active"} />
              <StepRow label="Skriv ned talen" sub="OpenAI Whisper · norsk" status={aiStep > 2 ? "done" : aiStep === 2 ? "active" : "todo"} />
              <StepRow label="Fyller ut rapporten" sub="Finn maskin, arbeid, avvik og neste service" status={aiStep === 3 ? "active" : "todo"} />
            </div>
            <p className="text-center text-xs text-muted-foreground">Opptaket er trygt lagra sjølv om AI-steget skulle feile.</p>
          </ResponsiveDialogBody>
        )}

        {/* ---------------- ERROR ---------------- */}
        {phase === "error" && (
          <>
            <ResponsiveDialogBody className="md:px-6 md:py-5">
              <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm" data-testid="voice-error">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <span>{errMsg}</span>
              </div>
            </ResponsiveDialogBody>
            <ResponsiveDialogFooter className="md:border-t md:px-6 md:py-3">
              <Button variant="outline" onClick={() => { rec.reset(); setFileRec(null); setPhase("record"); }} data-testid="voice-rerecord-button"><RotateCcw /> Ta opp på nytt</Button>
              <Button onClick={retryAnalysis} data-testid="voice-retry-button"><Sparkles /> Prøv igjen</Button>
            </ResponsiveDialogFooter>
          </>
        )}

        {/* ---------------- REVIEW ---------------- */}
        {phase === "review" && result && (
          <>
            <ResponsiveDialogBody className="md:min-h-0 md:flex-1 md:overflow-y-auto md:px-6 md:py-4">
              {suggestsOther && (
                <button type="button" onClick={switchForm} className="flex w-full items-center gap-3 rounded-xl border border-primary/40 bg-primary/10 p-3 text-left text-sm" data-testid="voice-switch-form">
                  <ArrowRightLeft className="h-4 w-4 shrink-0 text-primary" />
                  <span className="flex-1">Dette høyrest ut som ein <b>{otherType === "jobb" ? "jobbrapport" : "servicerapport"}</b>. Trykk for å byte skjema – opptaket følgjer med.</span>
                </button>
              )}

              {/* Machine */}
              <Section id="machine" title="Maskin" include={matched ? include.machine && useMachine : undefined} onToggle={matched ? (v) => { setUseMachine(v); setInclude((i) => ({ ...i, machine: v })); } : undefined}>
                {matched ? (
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted"><Wrench className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold" data-testid="voice-machine-label">{matched.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">{matched.customer_name || "—"}{result.machine?.match_reason ? ` · ${result.machine.match_reason}` : ""}</span>
                    </span>
                    <Confidence level={result.machine?.match_confidence} />
                  </div>
                ) : (
                  <div className="text-muted-foreground" data-testid="voice-machine-none">
                    Fann ikkje maskina i registeret.
                    {(result.machine?.brand || result.machine?.model || result.machine?.internal_number || result.machine?.customer_name) && (
                      <> Høyrde: <b className="text-foreground">{[result.machine.brand, result.machine.model, result.machine.internal_number && `#${result.machine.internal_number}`, result.machine.customer_name].filter(Boolean).join(" · ")}</b>.</>
                    )}{" "}
                    Vel maskin manuelt i skjemaet.
                  </div>
                )}
              </Section>

              {reportType === "service" ? (
                <>
                  {(result.service_type || result.machine_hours != null) && (
                    <div className="grid grid-cols-2 gap-2 [&>*]:min-w-0">
                      {result.service_type && (
                        <Section id="service_type" title="Servicetype" include={include.service_type} onToggle={(v) => setInclude((i) => ({ ...i, service_type: v }))}>
                          <span className="font-semibold">{result.service_type}</span>
                        </Section>
                      )}
                      {result.machine_hours != null && (
                        <Section id="hours" title="Timetal" include={include.hours} onToggle={(v) => setInclude((i) => ({ ...i, hours: v }))}>
                          <span className="font-semibold tabular-nums">{num(result.machine_hours)} t</span>
                        </Section>
                      )}
                    </div>
                  )}
                  {(result.checklist_done || []).length > 0 && (
                    <Section id="checklist" title={`Sjekkliste · ${result.checklist_done.length} punkt`} include={include.checklist} onToggle={(v) => setInclude((i) => ({ ...i, checklist: v }))}>
                      <ul className="space-y-1">
                        {result.checklist_done.map((idx) => (
                          <li key={idx} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{checklist[idx]}</li>
                        ))}
                      </ul>
                    </Section>
                  )}
                  {(result.work_performed || []).length > 0 && (
                    <Section id="work" title="Anna arbeid" include={include.work} onToggle={(v) => setInclude((i) => ({ ...i, work: v }))}>
                      <ul className="list-disc space-y-1 pl-4">{result.work_performed.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </Section>
                  )}
                  {(result.parts_used || []).length > 0 && (
                    <Section id="parts" title="Deler og væsker brukt" include={include.parts} onToggle={(v) => setInclude((i) => ({ ...i, parts: v }))}>
                      <ul className="space-y-0.5">
                        {result.parts_used.map((p, i) => (
                          <li key={i} className="flex justify-between gap-3"><span>{p.name}</span><span className="tabular-nums text-muted-foreground">{p.quantity != null ? `${num(p.quantity)} ${p.unit || ""}` : ""}</span></li>
                        ))}
                      </ul>
                      <p className="mt-1.5 text-xs text-muted-foreground">Blir lagt inn som linjer under «Anna arbeid».</p>
                    </Section>
                  )}
                  {result.defects && (
                    <Section id="defects" title="Avvik" include={include.defects} onToggle={(v) => setInclude((i) => ({ ...i, defects: v }))}>
                      <p className="whitespace-pre-line">{result.defects}</p>
                    </Section>
                  )}
                  {(result.next_service_hours != null || result.next_service_date) && (
                    <Section id="next" title="Neste service" include={include.next} onToggle={(v) => setInclude((i) => ({ ...i, next: v }))}>
                      <span className="font-semibold tabular-nums">
                        {[result.next_service_hours != null && `${num(result.next_service_hours)} t`, result.next_service_date && formatDate(result.next_service_date)].filter(Boolean).join(" · ")}
                      </span>
                    </Section>
                  )}
                  {result.control_done && (
                    <Section id="control" title="Årskontroll utført" include={include.control} onToggle={(v) => setInclude((i) => ({ ...i, control: v }))}>
                      Set ny årskontroll-frist eitt år fram.
                    </Section>
                  )}
                </>
              ) : (
                <>
                  {result.title && (
                    <Section id="title" title="Tittel" include={include.title} onToggle={(v) => setInclude((i) => ({ ...i, title: v }))}><span className="font-semibold">{result.title}</span></Section>
                  )}
                  {result.description && (
                    <Section id="description" title="Skildring" include={include.description} onToggle={(v) => setInclude((i) => ({ ...i, description: v }))}><p className="whitespace-pre-line">{result.description}</p></Section>
                  )}
                  {(result.work_performed || []).length > 0 && !result.description && (
                    <Section id="work" title="Utført" include={include.work} onToggle={(v) => setInclude((i) => ({ ...i, work: v }))}>
                      <ul className="list-disc space-y-1 pl-4">{result.work_performed.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </Section>
                  )}
                  <div className="grid grid-cols-2 gap-2 [&>*]:min-w-0">
                    {result.hours_spent != null && (
                      <Section id="hours_spent" title="Timar brukt" include={include.hours_spent} onToggle={(v) => setInclude((i) => ({ ...i, hours_spent: v }))}><span className="font-semibold tabular-nums">{num(result.hours_spent)} t</span></Section>
                    )}
                    {result.machine_hours != null && (
                      <Section id="hours" title="Timetal maskin" include={include.hours} onToggle={(v) => setInclude((i) => ({ ...i, hours: v }))}><span className="font-semibold tabular-nums">{num(result.machine_hours)} t</span></Section>
                    )}
                    {result.location && (
                      <Section id="location" title="Stad" include={include.location} onToggle={(v) => setInclude((i) => ({ ...i, location: v }))}><span className="font-semibold">{result.location}</span></Section>
                    )}
                  </div>
                  {(result.defects || result.follow_up) && (
                    <Section id="follow_up" title="Merknader / oppfølging" include={include.follow_up || include.defects} onToggle={(v) => setInclude((i) => ({ ...i, follow_up: v, defects: v }))}>
                      <p className="whitespace-pre-line">{[result.defects, result.follow_up].filter(Boolean).join("\n")}</p>
                    </Section>
                  )}
                </>
              )}

              {(result.uncertain || []).length > 0 && (
                <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--status-upcoming))]/30 bg-[rgb(var(--status-upcoming))]/10 p-3 text-xs" data-testid="voice-uncertain">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--status-upcoming))]" />
                  <ul className="space-y-0.5">{result.uncertain.map((u, i) => <li key={i}>{u}</li>)}</ul>
                </div>
              )}

              <Collapsible open={showTranscript} onOpenChange={setShowTranscript}>
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-sm hover:bg-accent" data-testid="voice-transcript-toggle">
                    <span className="flex items-center gap-2 font-medium"><FileAudio className="h-4 w-4 text-muted-foreground" /> Opptak og transkripsjon{result.duration_s ? ` · ${formatClock(result.duration_s)}` : ""}</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", showTranscript && "rotate-180")} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-2">
                  {(fileRec?.id || result.file_id) && (
                    <audio controls preload="none" src={fileUrl(fileRec?.id || result.file_id)} className="h-10 w-full" data-testid="voice-audio-player" />
                  )}
                  <p className="rounded-lg bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground" data-testid="voice-transcript">{result.transcript}</p>
                </CollapsibleContent>
              </Collapsible>
            </ResponsiveDialogBody>
            <ResponsiveDialogFooter className="grid grid-cols-[auto_1fr] gap-2 md:flex md:border-t md:px-6 md:py-3">
              <Button variant="outline" onClick={() => { rec.reset(); setFileRec(null); setResult(null); setPhase("record"); }} data-testid="voice-rerecord-button" aria-label="Ta opp på nytt"><RotateCcw /> <span className="hidden sm:inline">Ta opp på nytt</span></Button>
              <Button onClick={apply} data-testid="voice-apply-button"><Check /> Bruk i rapporten</Button>
            </ResponsiveDialogFooter>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
